/**
 * Willtec Vehicle Compatibility Cascading Search
 * Native Shopify Search & Discovery integration
 * Figma Node: 37:2284
 */
(() => {
  if (customElements.get('willtec-vehicle-search')) return;

  class WilltecVehicleSearch extends HTMLElement {
  constructor() {
    super();
    this.cache = new Map();
    this.currentAbort = null;
  }

  connectedCallback() {
    this.form = this.querySelector('[data-vehicle-form]') || this.querySelector('form');
    this.brandSelect = this.querySelector('[data-vehicle-brand]');
    this.modelSelect = this.querySelector('[data-vehicle-model]');
    this.yearSelect = this.querySelector('[data-vehicle-year]');
    this.submitBtn = this.querySelector('[data-vehicle-submit]');
    this.statusEl = this.querySelector('[data-vehicle-status]');

    this.searchUrl = this.dataset.searchUrl || (window.Shopify?.routes?.root || '/') + 'search';
    this.sectionId = this.dataset.sectionId || 'willtec-vehicle-search-data';

    if (!this.brandSelect || !this.modelSelect || !this.yearSelect || !this.submitBtn) {
      console.warn('[willtec-vehicle-search] Elementos obrigatórios não encontrados');
      return;
    }

    this.handleBrandChange = this.onBrandChange.bind(this);
    this.handleModelChange = this.onModelChange.bind(this);
    this.handleYearChange = this.onYearChange.bind(this);
    this.handleSubmit = this.onSubmit.bind(this);

    this.brandSelect.addEventListener('change', this.handleBrandChange);
    this.modelSelect.addEventListener('change', this.handleModelChange);
    this.yearSelect.addEventListener('change', this.handleYearChange);

    if (this.form) {
      this.form.addEventListener('submit', this.handleSubmit);
    } else {
      this.submitBtn.addEventListener('click', this.handleSubmit);
    }

    this.fetchInitialBrands();
  }

  disconnectedCallback() {
    if (this.currentAbort) {
      this.currentAbort.abort();
    }
    if (this.brandSelect) {
      this.brandSelect.removeEventListener('change', this.handleBrandChange);
    }
    if (this.modelSelect) {
      this.modelSelect.removeEventListener('change', this.handleModelChange);
    }
    if (this.yearSelect) {
      this.yearSelect.removeEventListener('change', this.handleYearChange);
    }
    if (this.form) {
      this.form.removeEventListener('submit', this.handleSubmit);
    } else if (this.submitBtn) {
      this.submitBtn.removeEventListener('click', this.handleSubmit);
    }
  }

  showStatus(message, isError = false) {
    if (!this.statusEl) return;
    this.statusEl.textContent = message;
    if (isError) {
      this.statusEl.classList.remove('hidden', 'text-zinc-600');
      this.statusEl.classList.add('text-red-600');
    } else {
      this.statusEl.classList.remove('hidden', 'text-red-600');
      this.statusEl.classList.add('text-zinc-600');
    }
  }

  clearStatus() {
    if (!this.statusEl) return;
    this.statusEl.textContent = '';
    this.statusEl.classList.add('hidden');
  }

  setSelectOptions(selectEl, items, placeholderText = 'Selecione uma opção') {
    selectEl.innerHTML = '';

    const defaultOption = document.createElement('option');
    defaultOption.value = '';
    defaultOption.textContent = placeholderText;
    defaultOption.selected = true;
    defaultOption.disabled = true;
    selectEl.appendChild(defaultOption);

    if (Array.isArray(items)) {
      items.forEach(item => {
        if (!item || !item.value) return;
        const option = document.createElement('option');
        option.value = String(item.value);
        option.textContent = String(item.label || item.value);
        selectEl.appendChild(option);
      });
    }
  }

  async fetchFilterData(params = {}) {
    if (this.currentAbort) {
      this.currentAbort.abort();
    }
    this.currentAbort = new AbortController();

    const url = new URL(this.searchUrl, window.location.origin);
    url.searchParams.set('q', '*');
    url.searchParams.set('type', 'product');
    url.searchParams.set('section_id', this.sectionId);

    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, String(value));
      }
    }

    const response = await fetch(url.toString(), {
      signal: this.currentAbort.signal,
      headers: {
        'Accept': 'text/html,application/xhtml+xml',
        'X-Requested-With': 'XMLHttpRequest'
      }
    });

    if (!response.ok) {
      throw new Error(`Erro na resposta do servidor (${response.status})`);
    }

    const html = await response.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const jsonScript = doc.querySelector('[data-vehicle-json]');

    if (!jsonScript) {
      throw new Error('Formato de dados inválido retornado');
    }

    return JSON.parse(jsonScript.textContent);
  }

  async fetchInitialBrands() {
    this.clearStatus();
    this.brandSelect.disabled = true;
    this.brandSelect.setAttribute('aria-busy', 'true');
    this.setSelectOptions(this.brandSelect, [], 'Carregando marcas...');

    try {
      let brands;
      if (this.cache.has('__brands__')) {
        brands = this.cache.get('__brands__');
      } else {
        const data = await this.fetchFilterData();
        brands = data.brands || [];
        this.cache.set('__brands__', brands);
      }

      brands.sort((a, b) => {
        const nameA = String(a.label || a.value);
        const nameB = String(b.label || b.value);
        return nameA.localeCompare(nameB, 'pt-BR', { sensitivity: 'base' });
      });

      this.setSelectOptions(this.brandSelect, brands, 'Selecione a marca');
      this.brandSelect.disabled = false;
      this.brandSelect.removeAttribute('aria-busy');
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('[willtec-vehicle-search] Erro ao carregar marcas:', err);
      this.setSelectOptions(this.brandSelect, [], 'Selecione a marca');
      this.brandSelect.disabled = false;
      this.brandSelect.removeAttribute('aria-busy');
      this.showStatus('Não foi possível carregar as marcas. Tente recarregar a página.', true);
    }
  }

  async onBrandChange() {
    this.clearStatus();
    const brand = this.brandSelect.value;

    // Reset downstream fields
    this.setSelectOptions(this.modelSelect, [], 'Selecione a marca primeiro');
    this.modelSelect.disabled = true;
    this.modelSelect.removeAttribute('aria-busy');

    this.setSelectOptions(this.yearSelect, [], 'Selecione o modelo primeiro');
    this.yearSelect.disabled = true;
    this.yearSelect.removeAttribute('aria-busy');

    this.submitBtn.disabled = true;

    if (!brand) return;

    this.modelSelect.disabled = true;
    this.modelSelect.setAttribute('aria-busy', 'true');
    this.setSelectOptions(this.modelSelect, [], 'Carregando modelos...');

    try {
      let models;
      const cacheKey = `brand:${brand}`;
      if (this.cache.has(cacheKey)) {
        models = this.cache.get(cacheKey);
      } else {
        const data = await this.fetchFilterData({
          'filter.p.m.custom.montadora': brand
        });
        models = data.models || [];
        this.cache.set(cacheKey, models);
      }

      if (models.length === 0) {
        this.setSelectOptions(this.modelSelect, [], 'Nenhum modelo disponível');
        this.modelSelect.disabled = true;
        this.modelSelect.removeAttribute('aria-busy');
        this.showStatus('Nenhum modelo compatível encontrado para esta marca.', false);
        return;
      }

      models.sort((a, b) => {
        const nameA = String(a.label || a.value);
        const nameB = String(b.label || b.value);
        return nameA.localeCompare(nameB, 'pt-BR', { sensitivity: 'base', numeric: true });
      });

      this.setSelectOptions(this.modelSelect, models, 'Selecione o modelo');
      this.modelSelect.disabled = false;
      this.modelSelect.removeAttribute('aria-busy');
      this.modelSelect.focus();
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('[willtec-vehicle-search] Erro ao carregar modelos:', err);
      this.setSelectOptions(this.modelSelect, [], 'Selecione o modelo');
      this.modelSelect.disabled = true;
      this.modelSelect.removeAttribute('aria-busy');
      this.showStatus('Não foi possível carregar as opções de modelo. Tente novamente.', true);
    }
  }

  async onModelChange() {
    this.clearStatus();
    const brand = this.brandSelect.value;
    const model = this.modelSelect.value;

    // Reset downstream fields
    this.setSelectOptions(this.yearSelect, [], 'Selecione o modelo primeiro');
    this.yearSelect.disabled = true;
    this.yearSelect.removeAttribute('aria-busy');

    this.submitBtn.disabled = true;

    if (!brand || !model) return;

    this.yearSelect.disabled = true;
    this.yearSelect.setAttribute('aria-busy', 'true');
    this.setSelectOptions(this.yearSelect, [], 'Carregando anos...');

    try {
      let years;
      const cacheKey = `model:${brand}|${model}`;
      if (this.cache.has(cacheKey)) {
        years = this.cache.get(cacheKey);
      } else {
        const data = await this.fetchFilterData({
          'filter.p.m.custom.montadora': brand,
          'filter.p.m.custom.modelo_veiculo': model
        });
        years = data.years || [];
        this.cache.set(cacheKey, years);
      }

      if (years.length === 0) {
        this.setSelectOptions(this.yearSelect, [], 'Nenhum ano disponível');
        this.yearSelect.disabled = true;
        this.yearSelect.removeAttribute('aria-busy');
        this.showStatus('Nenhum ano compatível encontrado para este modelo.', false);
        this.submitBtn.disabled = false;
        return;
      }

      // Sort years: numeric descending first, non-numeric (e.g. "Todos os anos") at the end
      years.sort((a, b) => {
        const strA = String(a.value || a.label).trim();
        const strB = String(b.value || b.label).trim();
        const numA = parseInt(strA, 10);
        const numB = parseInt(strB, 10);
        const isNumA = !isNaN(numA) && /^\d{4}$/.test(strA);
        const isNumB = !isNaN(numB) && /^\d{4}$/.test(strB);

        if (isNumA && isNumB) {
          return numB - numA;
        }
        if (isNumA) return -1;
        if (isNumB) return 1;
        return strA.localeCompare(strB, 'pt-BR');
      });

      this.setSelectOptions(this.yearSelect, years, 'Selecione o ano');
      this.yearSelect.disabled = false;
      this.yearSelect.removeAttribute('aria-busy');
      this.yearSelect.focus();
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('[willtec-vehicle-search] Erro ao carregar anos:', err);
      this.setSelectOptions(this.yearSelect, [], 'Selecione o ano');
      this.yearSelect.disabled = true;
      this.yearSelect.removeAttribute('aria-busy');
      this.showStatus('Não foi possível carregar as opções de ano. Tente novamente.', true);
    }
  }

  onYearChange() {
    this.clearStatus();
    const brand = this.brandSelect.value;
    const model = this.modelSelect.value;
    const year = this.yearSelect.value;

    if (brand && model && (year || this.yearSelect.disabled)) {
      this.submitBtn.disabled = false;
    } else {
      this.submitBtn.disabled = true;
    }
  }

  onSubmit(e) {
    if (e) {
      e.preventDefault();
    }

    const brand = this.brandSelect.value;
    const model = this.modelSelect.value;
    const year = this.yearSelect.value;

    if (!brand) {
      this.showStatus('Por favor, selecione a marca do veículo para buscar.', true);
      return;
    }

    if (!model) {
      this.showStatus('Por favor, selecione o modelo do veículo para buscar.', true);
      return;
    }

    // Se o campo de ano estiver habilitado, exige que o usuário selecione o ano
    if (!this.yearSelect.disabled && !year) {
      this.showStatus('Por favor, selecione o ano do veículo para buscar.', true);
      return;
    }

    const targetUrl = new URL(this.searchUrl, window.location.origin);
    targetUrl.searchParams.set('q', '*');
    targetUrl.searchParams.set('type', 'product');
    targetUrl.searchParams.set('filter.p.m.custom.montadora', brand);
    if (model) {
      targetUrl.searchParams.set('filter.p.m.custom.modelo_veiculo', model);
    }
    if (year) {
      targetUrl.searchParams.set('filter.p.m.custom.ano_veiculo', year);
    }

    window.location.href = targetUrl.toString();
  }
}

  if (!customElements.get('willtec-vehicle-search')) {
    customElements.define('willtec-vehicle-search', WilltecVehicleSearch);
  }
})();
